import styled from '@theme/styled-components'
import * as React from 'react'

import useI18n from '@store/i18n/useI18n'
import * as AccountStore from '@store/account/account'

import AnimateHeight from 'react-animate-height'
import Button from '@components/button/button'

import oauth from '@utils/OAuth2'

const MENU_HEIGHT = 620
const MENU_WIDTH = 600
const ANIMATION_DURATION = 500

interface Props {
  closeAccount: () => void
}

const Account = ({ closeAccount }: Props) => {
  const i18n = useI18n()
  const account = AccountStore.store.getState().account

  const [menuHeight, setMenuHeight] = React.useState<number>(0)

  React.useEffect(() => {
    setMenuHeight(MENU_HEIGHT)
  }, [])

  const hideAccount = () => {
    setMenuHeight(-1)
    setTimeout(closeAccount, ANIMATION_DURATION)
  }

  if (!account) {
    return null
  }

  return (
    <>
      <Background onClick={hideAccount} />
      <Container id="menu-panel" duration={ANIMATION_DURATION} height={menuHeight}>
        <ComponentTitleContainer>
          <LeftContainer>
            <ComponentTitle>{i18n.t('components.account.title')}</ComponentTitle>
          </LeftContainer>
          <Button
            title={i18n.t('components.account.logout')}
            type="navigation"
            status="active"
            onClick={oauth.logout}
            // size="small"
          />
        </ComponentTitleContainer>
        <Content>
          <Row>
            <Item>
              <Title>{i18n.t('components.account.firstname')}</Title>
              <Value>{account.firstname}</Value>
            </Item>
            <Item>
              <Title>{i18n.t('components.account.lastname')}</Title>
              <Value>{account.lastname}</Value>
            </Item>
          </Row>
          <Row>
            <Item>
              <Title>{i18n.t('components.account.email')}</Title>
              <Value>{account.email}</Value>
            </Item>
            <Item>
              <Title>{i18n.t('components.account.role')}</Title>
              <Value>{i18n.t(`userRoles.${account.role}`)}</Value>
            </Item>
          </Row>
          <Row>
            <Item>
              <Title>{i18n.t('components.account.regions')}</Title>
              <RegionsContainer>
                {account.regions.map((itm, idx) => (
                  <RegionContainer key={idx}>
                    <Value style={{ marginLeft: 10, color: '#fff' }}>{itm.label}</Value>
                  </RegionContainer>
                ))}
              </RegionsContainer>
            </Item>
          </Row>
        </Content>
      </Container>
    </>
  )
}

export default Account

const Background = styled('div')`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  background-color: #0005;
  cursor: pointer;

  z-index: 2;
`
const Container = styled(AnimateHeight)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  z-index: 3;
  width: ${MENU_WIDTH}px;
  margin: auto;
  padding: 15px;
  border-radius: 30px;
  background-color: #ffffff;
  box-shadow: 0 12px 35px 0 rgba(33, 75, 115, 0.2);
`
const LeftContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
const Content = styled('div')`
  height: ${MENU_HEIGHT}px;
  overflow: scroll;
`
const ComponentTitleContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-directin: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  border: 0px solid #0053a1;
  border-bottom-width: 3px;
`
const ComponentTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
  align-items: center;
  line-height: 25px;
  font-size: 25px;
  display: flex;
  height: 50px;

  margin-left: 25px;
`
const Row = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 40px 25px;
  padding: 15px 25px;
  border-radius: 20px;
  box-shadow: 0 12px 35px 0 rgba(33, 75, 115, 0.2);
`
const Item = styled('div')`
  flex: 1;
  display: flex;
  min-height: 100px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-around;
`
const Title = styled('div')`
  ${(props) => props.theme.fonts.title};
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: ${(props) => props.theme.colors.secondary_yellow};
`
const Value = styled('div')`
  ${(props) => props.theme.fonts.label};
  color: ${(props) => props.theme.colors.secondary_grey};
`
const RegionsContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin-top: 25px;
`
const RegionContainer = styled('div')`
  display: flex;
  padding: 3px 20px;
  border-radius: 25px;
  align-items: center;
  margin: 5px 10px 5px 0;
  background-color: ${(props) => props.theme.colors.primary_turquoise};
`
